import {
  Box,
  Field,
  Flex,
  Heading,
  Input,
  Message,
  SwitchRoot,
  SwitchThumb,
  Text,
  Textarea,
} from 'ui'
import { Footer } from '../Footer'
import { Controller, useForm } from 'react-hook-form'
import { DetailsForm, detailsSchema } from '../schemas'
import { zodResolver } from '@hookform/resolvers/zod'
import { usePositionEditor } from '../usePositionEditor'
import { useDebounceEffect } from 'ahooks'
import { useAuthContext } from 'components/auth'
import { useGateValue } from '@statsig/react-bindings'
import { FeatureGates } from 'lib/statsig/feature_gates'
import { ShiftLeadInfoModal } from 'pages/ShiftEditorPage/pages/StaffPage/ShiftLeads/ShiftLeadInfoModal'

export function Details() {
  const { positionData, setPositionData, editorError, submitPositionData } =
    usePositionEditor()
  const { company } = useAuthContext()
  const isW2LeadPositionsEnabled = useGateValue(
    FeatureGates.EnableW2LeadPositions
  )
  const {
    watch,
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<DetailsForm>({
    resolver: zodResolver(detailsSchema),
    defaultValues: positionData.details,
  })
  const positionName = watch('positionName')
  const description = watch('description')
  const isShiftLeadPosition = watch('isShiftLeadPosition')

  useDebounceEffect(
    () => {
      // auto-save input values
      setPositionData({
        details: {
          positionName,
          description,
          isShiftLeadPosition,
        },
      })
    },
    [positionName, description, isShiftLeadPosition],
    { wait: 500 }
  )

  return (
    <form
      onSubmit={(e) => {
        e.stopPropagation()
        e.preventDefault()
        handleSubmit(async (data) => {
          await setPositionData({
            details: data,
          })
          submitPositionData()
        })(e)
      }}
    >
      <Box width={[1, '68rem']}>
        <Heading level={3}>Provide Job Details</Heading>
        <Field
          label={'Position Name'}
          error={errors.positionName?.message?.toString()}
        >
          <Input
            placeholder={'Enter Position Name'}
            {...register('positionName')}
          />
        </Field>
        <Field
          label={'Job Description'}
          error={errors.description?.message?.toString()}
        >
          <Textarea
            minRows={15}
            placeholder={'Write a description of the shift'}
            {...register('description')}
          />
        </Field>
        {company?.w2 && isW2LeadPositionsEnabled ? (
          <Controller
            name={'isShiftLeadPosition'}
            control={control}
            render={({ field }) => (
              <Flex alignItems={'center'} mb={3}>
                <SwitchRoot
                  checked={!!field.value}
                  onCheckedChange={field.onChange}
                >
                  <SwitchThumb />
                </SwitchRoot>
                <Text ml={2}>
                  Is this a shift lead position?{' '}
                  <ShiftLeadInfoModal />
                </Text>
              </Flex>
            )}
          />
        ) : null}
        {editorError ? (
          <Message mb={3} variant={'error'}>
            {editorError}
          </Message>
        ) : null}
        <Footer ctaText={'Save'} />
      </Box>
    </form>
  )
}
